// Загрузка моделей оружия (GLB, Draco) и точек хвата из weapons.json.
// Одна и та же модель идёт в вид от первого лица, в руки другим игрокам и
// ботам и на пол подбором — единый стиль.

import type { Renderer } from "../engine/renderer";
import type { LoadedMesh } from "../engine/meshLoader";
import { WEAPONS } from "./catalog";

export interface WeaponPoints {
  muzzle: [number, number, number];
  grip_r: [number, number, number];
  grip_l: [number, number, number];
  bbox_min: [number, number, number];
  bbox_max: [number, number, number];
}

const BASE = "/models/weapons/";

export class WeaponAssets {
  private meshes = new Map<string, LoadedMesh>();
  private pending = new Map<string, Promise<LoadedMesh | null>>();
  points = new Map<string, WeaponPoints>();
  private pointsReady: Promise<void>;

  constructor(private renderer: Renderer) {
    this.pointsReady = fetch(BASE + "weapons.json")
      .then((r) => (r.ok ? r.json() : {}))
      .then((j: Record<string, WeaponPoints>) => {
        for (const [k, v] of Object.entries(j)) this.points.set(k, v);
      })
      .catch(() => { /* без точек — хват по умолчанию */ });
  }

  /** Модель по ключу (rifle, hand_r, forearm…) — null, пока не загружена. */
  get(key: string): LoadedMesh | null {
    const m = this.meshes.get(key);
    if (m) return m;
    if (!this.pending.has(key)) void this.load(key);
    return null;
  }

  load(key: string): Promise<LoadedMesh | null> {
    const p0 = this.pending.get(key);
    if (p0) return p0;
    const p = this.renderer.loadMeshFromUrl(BASE + key + ".glb")
      .then((m) => { this.meshes.set(key, m); return m; })
      .catch((e) => { console.warn(`[weapons] ${key}.glb:`, e); return null; });
    this.pending.set(key, p);
    return p;
  }

  /** Модель оружия по id (кулаки — null). */
  weaponMesh(id: number): LoadedMesh | null {
    const def = WEAPONS[id];
    if (!def || !def.model) return null;
    return this.get(def.key);
  }

  pts(id: number): WeaponPoints | null {
    const def = WEAPONS[id];
    return def ? this.points.get(def.key) ?? null : null;
  }

  /** Подгрузить всё заранее (руки + оружие) — после старта, в фоне. */
  async preload(): Promise<void> {
    await this.pointsReady;
    const keys = ["hand_r", "hand_l", "forearm", "muzzle_flash", "rocket_proj"];
    for (const d of WEAPONS) if (d.model) keys.push(d.key);
    await Promise.all(keys.map((k) => this.load(k)));
  }
}
